import { useState } from 'react'
import MFAccordion from '../ui-components/MFAccordion'
import RenderHTML from '../ui-components/RenderHTML'
import { trackEvent } from '../../utils/appBridge'
import { EventNames } from '../../types/misc.types'
import { useEliteBuyCtaExtras } from './EliteBuyCtaExtrasContext'

type FAQType = {
  title: string
  descp: string
}

const faqs = [
  {
    title: 'What is the Elite membership and who is it for?',
    descp: `Elite is a yearly membership built for <b>investors who want to take their mutual fund portfolio to the next level.</b> It removes guesswork and <b>helps you make smarter, data-backed decisions at every stage of your investing journey.</b>`
  },
  {
    title: 'What exactly do I get with Elite membership?',
    descp: `Elite gives you access to:
      <ul style="list-style-type: disc; padding-left: 20px;">
      <li><b>Power Rank</b> – Know the <b>exact ranks of any mutual fund within its category</b></li>
      <li><b>Elite Scores</b> – Get a 3-dimensional view of each fund across metrics like <b>Consistency, Volatility, and Recency</b> - so you don't rely on returns alone</li>
      <li><b>Rank Trends</b> – Track <b>how a fund has ranked over time</b> (Rolling Returns and Trailing Returns).</li>
      <li><b>Power Rebalancing</b> – A tax-efficient <b>personalised plan that helps you switch from underperforming funds to stronger ones</b> - without changing your risk profile.</li>
      <li><b>Family Portfolios (Coming soon)</b> - Manage all family investments in one app</li>
      </ul>`
  },
  {
    title: 'Does Elite guarantee higher returns?',
    descp: `No investment service can guarantee that - and neither do we. <b> What Elite offers is a research-backed framework that helps you consistently make better decisions </b> (choosing stronger funds, exiting underperforming funds, avoiding unnecessary churn).`
  },
  {
    title: 'Will Elite recommend completely new funds or only work with my existing portfolio?',
    descp: `Both. You can use <b>Power Rank and Elite Scores to discover top-ranked funds before making an investment decision.</b> Plus, Elite will continuously monitor your portfolio and recommend rebalancing only when a better alternative exists in the same category.`
  },
  {
    title: 'How often are the rankings and insights updated?',
    descp:
      'All rankings (Power Rank, Elite Scores, Rank Trends) are reviewed by experts & updated <b>monthly</b>, based on the latest fund and category data.'
  },
  {
    title: 'Can I pause or cancel my Elite membership?',
    descp: `Elite is a <b>one-time purchase for 12 months</b> and cannot be paused or cancelled midway.
    That said, the rankings, insights and rebalancing plans keep updating throughout the year, including the addition of new features.`
  },
  {
    title: 'Will the membership auto-renew after 12 months?',
    descp: `No, <b>Elite does not auto-renew.</b> You'll be notified when your membership is about to end and can choose whether or not to renew.`
  },
  {
    title: 'Is my data secure?',
    descp:
      'Yes. We use <b>industry-grade encryption and follow strict data protection practices.</b> Your personal and financial data is never shared with third parties — under any circumstance.'
  }
] as FAQType[]

export const FAQs = ({ faqHeader = 'FAQs ' }) => {
  const [accordionState, setAccordionState] = useState<{ [key: string]: boolean }>({})
  const eliteBuyCtaExtras = useEliteBuyCtaExtras()
  return (
    <div className='bg-grey80 px-5 pt-5'>
      {faqHeader && <h2 className='text-[#6A6E6E] text-[13px] font-[400] pb-2 pl-4 m-0'>{faqHeader}</h2>}
      <ul className='rounded-[12px] flex flex-col gap-[1px] overflow-hidden list-none m-0 p-0'>
        {faqs?.map(({ title, descp }, index) => {
          return (
            <li key={`${title}${index}`} className='bg-white list-none'>
              <MFAccordion
                title={title}
                accordionHeaderClassName='!px-4 !min-h-12'
                accordionBodyClassName='!px-4 max-w-[90%]'
                isExpanded={!!accordionState?.[title]}
                onAccordionClick={() => {
                  setAccordionState((accordionStatePrev) => {
                    trackEvent({
                      eventName: EventNames.UserClicked,
                      payload: {
                        ...eliteBuyCtaExtras,
                        eventId: title,
                        responseName: accordionStatePrev?.[title] ? 'Collapse' : 'Expand',
                        ScreenName: 'FAQ',
                        screenType: window?.location?.href?.includes('elite-dashboard')
                          ? 'MF_L2_Elite_Dashboard_Bottomsheet'
                          : 'MF_Elite_Landing'
                      }
                    })
                    return {
                      ...accordionStatePrev,
                      [title]: !accordionStatePrev?.[title]
                    }
                  })
                }}
              >
                <div className='text-[#6A6E6E] text-[13px] leading-[150%] pb-4'>
                  <RenderHTML innerHTMLText={descp} />{' '}
                </div>
              </MFAccordion>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
